import * as Sentry from "@sentry/react";
import config from "./config";

// Initialize Sentry
export const initSentry = () => {
  const dsn = config.SENTRY_URL;
  // Use config.SENTRY_ENV, fallback to NODE_ENV, then production
  const environment = config.SENTRY_ENV || process.env.NODE_ENV || "production";

  // Debug: Check if window._env_ exists (should NOT exist after migration)
  if (typeof window !== 'undefined' && window._env_) {
    console.warn("[Sentry] ⚠️ OLD CODE DETECTED: window._env_ still exists! Browser is using cached old bundle!");
  }

  // Only initialize if DSN is provided
  if (dsn) {
    try {
      Sentry.init({
        dsn: dsn,
        environment: environment,
        
        // Set tracesSampleRate to reduce payload size and avoid 413 errors
        // Reduced to 0.05 (5%) to prevent Content Too Large errors
        tracesSampleRate: 0.05,
        
        // Limit max breadcrumbs to reduce payload size
        maxBreadcrumbs: 30,
        
        // Capture unhandled promise rejections
        captureUnhandledRejections: true,
        
        // Capture uncaught exceptions
        beforeSend(event, hint) {
          // Force environment to be set (in case it's not being passed correctly)
          event.environment = environment;
          
          // Aggressively reduce payload size to avoid 413 errors
          
          // 1. Limit breadcrumbs (keep only last 20)
          if (event.breadcrumbs && event.breadcrumbs.length > 20) {
            event.breadcrumbs = event.breadcrumbs.slice(-20);
          }
          
          // 2. Truncate large breadcrumb messages
          if (event.breadcrumbs) {
            event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
              ...breadcrumb,
              message: breadcrumb.message && breadcrumb.message.length > 200 
                ? breadcrumb.message.substring(0, 200) + '...' 
                : breadcrumb.message,
              data: breadcrumb.data && typeof breadcrumb.data === 'object'
                ? Object.keys(breadcrumb.data).reduce((acc, key) => {
                    const value = breadcrumb.data[key];
                    if (typeof value === 'string' && value.length > 200) {
                      acc[key] = value.substring(0, 200) + '...';
                    } else {
                      acc[key] = value;
                    }
                    return acc;
                  }, {})
                : breadcrumb.data
            }));
          }
          
          // 3. Limit and truncate extra data
          if (event.extra) {
            const limitedExtra = {};
            let count = 0;
            for (const [key, value] of Object.entries(event.extra)) {
              if (count >= 10) break; // Only keep first 10 extra fields
              if (typeof value === 'string' && value.length > 500) {
                limitedExtra[key] = value.substring(0, 500) + '...';
              } else {
                limitedExtra[key] = value;
              }
              count++;
            }
            event.extra = limitedExtra;
          }
          
          // 4. Truncate exception stack traces and values
          if (event.exception && event.exception.values) {
            event.exception.values = event.exception.values.map(exception => ({
              ...exception,
              value: exception.value && exception.value.length > 500
                ? exception.value.substring(0, 500) + '...'
                : exception.value,
              stacktrace: exception.stacktrace ? {
                ...exception.stacktrace,
                frames: exception.stacktrace.frames 
                  ? exception.stacktrace.frames.slice(-50) // Keep only last 50 frames
                  : exception.stacktrace.frames
              } : exception.stacktrace
            }));
          }
          
          // 5. Remove or limit contexts that can be large
          if (event.contexts) {
            // Keep only essential contexts
            const essentialContexts = {};
            if (event.contexts.browser) {
              essentialContexts.browser = {
                name: event.contexts.browser.name,
                version: event.contexts.browser.version
              };
            }
            if (event.contexts.device) {
              essentialContexts.device = {
                name: event.contexts.device.name,
                model: event.contexts.device.model
              };
            }
            if (event.contexts.os) {
              essentialContexts.os = {
                name: event.contexts.os.name,
                version: event.contexts.os.version
              };
            }
            event.contexts = essentialContexts;
          }
          
          // 6. Remove large request data
          if (event.request) {
            event.request = {
              url: event.request.url,
              method: event.request.method,
              headers: event.request.headers ? Object.keys(event.request.headers).slice(0, 5).reduce((acc, key) => {
                acc[key] = event.request.headers[key];
                return acc;
              }, {}) : undefined
            };
          }
          
          // 7. Remove replay data if present (it's huge)
          if (event.replay_id) {
            delete event.replay_id;
          }
          
          return event;
        },
        
        // Set release version if available
        release: window.APP_VERSION || undefined,
        
        // Disable debug mode to prevent console spam
        debug: false,
        
        // Integrations
        integrations: [
          Sentry.browserTracingIntegration({
            // Reduce trace data
            enableLongTask: false,
            enableInp: false,
          }),
          // Disable replay integration to reduce payload size (it's very large)
          // Only enable if absolutely needed for debugging
          // Sentry.replayIntegration({
          //   maskAllText: false,
          //   blockAllMedia: false,
          // }),
        ],
        
        // Disable replay to reduce payload size
        // replaysSessionSampleRate: 0, // Disabled
        // replaysOnErrorSampleRate: 0, // Disabled
      });
      
    } catch (error) {
      // Sentry initialization failed
    }
  }
};

